/**
 * Onglet « IA » (API v2 `/ai`).
 *
 * Le filtre de service (`source`) est un PARAMÈTRE SERVEUR (contrat §4,
 * BUG-2) : changer de service relance la requête, et toutes les vues —
 * tuiles, séries, modèles, tableau, export — sont restreintes par le serveur.
 * Plus aucun filtrage côté client.
 *
 * Les coûts ne sont affichés qu'aux administrateurs (D-20,
 * `meta.viewer.can_view_costs`) ; pour les autres, le serveur ne les envoie
 * pas.
 */
import React, { useState } from 'react';
import { Card, Col, Input, Row, Segmented, Space, Table, Tag, Typography } from 'antd';
import { BulbOutlined, CommentOutlined, DollarOutlined, RocketOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
    AI_SOURCES,
    type AiParams,
    type AiResponse,
    type AiSourceFilter,
    type AiTiles,
} from '../../../api/monitoring/v2';
import { INK, cardStyle } from '../monitoringTheme';
import StatTile from '../shared/StatTile';
import CompactValue from '../shared/CompactValue';
import { CostText } from '../shared/MoneyText';
import EmptyState, { hasEmptyReason } from '../shared/EmptyState';
import TimeSeriesChart from '../shared/TimeSeriesChart';
import DataTable from '../shared/DataTable';
import ExportButton from '../shared/ExportButton';
import QueryStatusView from '../shared/QueryStatusView';
import MetaBanner from '../shared/MetaBanner';
import { useMonitoringQuery } from '../shared/useMonitoringQuery';
import { useMonitoringApiRef } from '../shared/useScopes';
import { useServerTable } from '../shared/useServerTable';
import { toCommonParams } from '../shared/useMonitoringFilters';
import { useFormat } from '../shared/format';
import type { MonitoringTabProps } from './PedagogyTab';

const { Text, Title } = Typography;

const NO_CHART_REASONS = ['future_period', 'no_ai_calls'] as const;
const ACTORS = ['student', 'staff', 'bench', 'system', 'total'] as const;

const BreakdownList: React.FC<{ entries: Array<[string, React.ReactNode]> }> = ({ entries }) => (
    <Space direction="vertical" size={0} style={{ fontSize: 12, fontWeight: 400 }}>
        {entries.map(([label, value]) => (
            <span key={label}>
                <Text style={{ color: INK.secondary, fontSize: 12 }}>{label} : </Text>
                <Text strong style={{ fontSize: 13 }}>
                    {value}
                </Text>
            </span>
        ))}
    </Space>
);

const AiTab: React.FC<MonitoringTabProps> = ({ filters, ready, active, refreshToken }) => {
    const { t } = useTranslation();
    const f = useFormat();
    const apiRef = useMonitoringApiRef();
    const [source, setSource] = useState<AiSourceFilter>('all');

    const common = toCommonParams(filters);
    const group = JSON.stringify(['ai', common, source]);
    const table = useServerTable(group);
    const modelsTable = useServerTable(group);
    const params: AiParams = { ...common, source, models: modelsTable.query, students: table.query };

    const query = useMonitoringQuery<AiResponse>(
        JSON.stringify(['ai', params]),
        (signal) => apiRef.current.getAi(params, signal),
        { refreshToken, enabled: active && ready, groupKey: group }
    );
    const data = query.data;

    const canViewCosts = !!data?.meta.viewer.can_view_costs;
    const tiles: AiTiles | undefined = data?.tiles;
    const count = (n: number) => <CompactValue value={n} />;

    /** Valeur d'une série monétaire : sa devise vient de la série, jamais d'un défaut. */
    const formatCostPoint = (raw: number | string, currency?: string) => {
        if (!currency) {
            // eslint-disable-next-line no-console
            console.warn('[monitoring] série de coût sans devise');
            return String(raw);
        }
        const amount = String(raw);
        return f.compact(amount, currency).text;
    };

    return (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Space size={8} wrap>
                <Text style={{ fontSize: 12, color: INK.secondary }}>{t('monitoring.source.label')}</Text>
                <Segmented
                    value={source}
                    aria-label={t('monitoring.source.label')}
                    onChange={(v) => setSource(v as AiSourceFilter)}
                    options={[
                        { label: t('monitoring.source.all'), value: 'all' },
                        ...AI_SOURCES.map((s) => ({ label: t(`monitoring.enum.ai_source.${s}`), value: s })),
                    ]}
                />
            </Space>

            <QueryStatusView query={query} tiles={6} />

            {data && tiles && (
                <>
                    <MetaBanner meta={data.meta} />
                    <EmptyState reasons={data.empty_reasons} />

                    <Row gutter={[12, 12]}>
                        <Col xs={12} sm={8} lg={6} xl={4}>
                            <StatTile metricId="IA-01" icon={<ThunderboltOutlined />} value={count(tiles.calls)} />
                        </Col>
                        {canViewCosts && tiles.cost && (
                            <Col xs={12} sm={8} lg={6} xl={4}>
                                <StatTile
                                    metricId="IA-02"
                                    icon={<DollarOutlined />}
                                    value={<CostText cost={tiles.cost} />}
                                    unpriced={{ no_price: tiles.unpriced.no_price, no_usage: tiles.unpriced.no_usage }}
                                />
                            </Col>
                        )}
                        <Col xs={12} sm={8} lg={6} xl={4}>
                            <StatTile
                                metricId="IA-03"
                                value={
                                    <BreakdownList
                                        entries={[
                                            [t('monitoring.unpriced.noPrice'), count(tiles.unpriced.no_price)],
                                            [t('monitoring.unpriced.noUsage'), count(tiles.unpriced.no_usage)],
                                        ]}
                                    />
                                }
                                extra={
                                    tiles.unpriced.models.length
                                        ? t('monitoring.unpriced.models', { models: tiles.unpriced.models.join(', ') })
                                        : undefined
                                }
                            />
                        </Col>
                        <Col xs={12} sm={8} lg={6} xl={4}>
                            <StatTile
                                metricId="IA-04"
                                value={
                                    <BreakdownList
                                        entries={(['input', 'cached', 'output', 'reasoning'] as const).map((k) => [
                                            t(`monitoring.tokens.${k}`),
                                            count(tiles.tokens[k]),
                                        ])}
                                    />
                                }
                            />
                        </Col>
                        <Col xs={12} sm={8} lg={6} xl={4}>
                            <StatTile metricId="IA-05" icon={<CommentOutlined />} value={count(tiles.questions)} />
                        </Col>
                        <Col xs={12} sm={8} lg={6} xl={4}>
                            <StatTile
                                metricId="IA-06"
                                icon={<BulbOutlined />}
                                value={
                                    <BreakdownList
                                        entries={(['quest', 'visual', 'exercise', 'illustration', 'reused'] as const).map((k) => [
                                            t(`monitoring.contents.${k}`),
                                            count(tiles.contents[k]),
                                        ])}
                                    />
                                }
                            />
                        </Col>
                        {tiles.undated_legacy && (
                            <Col xs={12} sm={8} lg={6} xl={4}>
                                <StatTile metricId="IA-UNDATED" icon={<RocketOutlined />} value={count(tiles.undated_legacy.calls)} />
                            </Col>
                        )}
                    </Row>

                    {tiles.by_actor && (
                        <Card style={cardStyle} title={<Title level={5} style={{ margin: 0 }}>{t('metric.IA-07.label')}</Title>}>
                            <Table
                                size="small"
                                pagination={false}
                                rowKey="actor"
                                dataSource={ACTORS.map((actor) => ({ actor, ...tiles.by_actor![actor] }))}
                                columns={[
                                    {
                                        key: 'actor',
                                        dataIndex: 'actor',
                                        title: t('monitoring.col.actor_kind'),
                                        render: (actor: string) =>
                                            actor === 'total' ? (
                                                <Text strong>{t('monitoring.enum.actor_kind.total')}</Text>
                                            ) : (
                                                <Tag bordered={false}>{t(`monitoring.enum.actor_kind.${actor}`)}</Tag>
                                            ),
                                    },
                                    {
                                        key: 'calls',
                                        dataIndex: 'calls',
                                        title: t('monitoring.col.calls'),
                                        align: 'end',
                                        render: (n: number) => count(n),
                                    },
                                    ...(canViewCosts
                                        ? [
                                              {
                                                  key: 'cost',
                                                  dataIndex: 'cost',
                                                  title: t('monitoring.col.cost'),
                                                  align: 'end' as const,
                                                  render: (cost: AiTiles['cost']) => (cost ? <CostText cost={cost} /> : '—'),
                                              },
                                          ]
                                        : []),
                                ]}
                            />
                        </Card>
                    )}

                    {!hasEmptyReason(data.empty_reasons, NO_CHART_REASONS) && (
                        <Row gutter={[16, 16]}>
                            <Col xs={24} xl={12}>
                                <TimeSeriesChart
                                    title={t('monitoring.charts.aiCalls')}
                                    kind="area"
                                    stacked
                                    series={data.series.filter((s) => s.metric === 'IA-01')}
                                    formatValue={(v) => f.compact(Number(v)).text}
                                />
                            </Col>
                            {canViewCosts && data.series.some((s) => s.metric === 'IA-02') && (
                                <Col xs={24} xl={12}>
                                    <TimeSeriesChart
                                        title={t('monitoring.charts.aiCost')}
                                        kind="area"
                                        stacked
                                        series={data.series.filter((s) => s.metric === 'IA-02')}
                                        formatValue={formatCostPoint}
                                    />
                                </Col>
                            )}
                            <Col xs={24} xl={12}>
                                <TimeSeriesChart
                                    title={t('monitoring.charts.questions')}
                                    series={data.series.filter((s) => s.metric === 'IA-05')}
                                    formatValue={(v) => f.compact(Number(v)).text}
                                />
                            </Col>
                        </Row>
                    )}

                    <Card
                        style={cardStyle}
                        title={<Title level={5} style={{ margin: 0 }}>{t('monitoring.table.models')}</Title>}
                        extra={<ExportButton dataset="ai_models" params={{ ...common, source, table: modelsTable.filters }} />}
                    >
                        <DataTable
                            table={data.models}
                            rowKey={(row) => String(row.model)}
                            loading={query.status === 'refreshing'}
                            server={{ state: modelsTable.state, onChange: modelsTable.setState }}
                            hiddenKeys={canViewCosts ? undefined : ['cost']}
                        />
                    </Card>

                    <Card
                        style={cardStyle}
                        title={
                            <Space size={12} wrap style={{ width: '100%', justifyContent: 'space-between' }}>
                                <Title level={5} style={{ margin: 0 }}>
                                    {t('monitoring.table.students')}
                                </Title>
                                <Space size={8} wrap>
                                    <Input.Search
                                        allowClear
                                        size="small"
                                        style={{ width: 220 }}
                                        aria-label={t('monitoring.table.search')}
                                        placeholder={t('monitoring.table.search')}
                                        defaultValue={table.q}
                                        onSearch={(value) => table.setQuery(value.trim())}
                                    />
                                    <ExportButton
                                        dataset="ai_students"
                                        params={{ ...common, source, table: table.filters }}
                                    />
                                </Space>
                            </Space>
                        }
                    >
                        <DataTable
                            table={data.students}
                            rowKey={(row) => String(row.id)}
                            loading={query.status === 'refreshing'}
                            server={{ state: table.state, onChange: table.setState }}
                            hiddenKeys={canViewCosts ? undefined : ['cost']}
                            emptyText={t('monitoring.noStudents')}
                        />
                    </Card>
                </>
            )}
        </Space>
    );
};

export default AiTab;
